import { useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { fmtMirrorDate } from '@/lib/dates';
import { useGroupAssets } from '@/hooks/useGroupAssets';
import { useGroupServiceJobs } from '@/hooks/useGroupServiceJobs';
import { kindLabel, statusLabel, type ServiceRequestKind, type ServiceRequestStatus } from '@/hooks/useServiceRequests';
import {
  ALL_STORES,
  ChainPage,
  EmptyRow,
  Pill,
  StoreCell,
  StoreFilterRow,
  Tile,
} from '@/components/chain/ChainShell';

type Tab = 'tickets' | 'visits';

/**
 * /service for a chain's corporate login — every store's tickets and every
 * store's Service Fusion visits, in one place.
 *
 * Two sources, deliberately kept apart on screen because they answer different
 * questions: a TICKET is something a store asked us for, a VISIT is work we
 * actually did. Merging them into one feed would make "nobody has asked for
 * anything" and "nobody has been out" indistinguishable.
 */
export default function ChainService() {
  const { data, isLoading } = useGroupAssets();
  const [tab, setTab] = useState<Tab>('tickets');
  const [store, setStore] = useState<string>(ALL_STORES);
  const [term, setTerm] = useState('');

  // Visits are a live per-store Service Fusion sweep, so it only runs when
  // someone actually opens that tab.
  const visitsEnabled = tab === 'visits';
  const visits = useGroupServiceJobs(90, 'all', visitsEnabled);

  const tickets = useMemo(() => {
    let out = data?.service_requests ?? [];
    if (store !== ALL_STORES) out = out.filter((r) => r.customer_id === store);
    const q = term.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        [r.subject, r.body, r.store_name].filter(Boolean).some((v) =>
          String(v).toLowerCase().includes(q),
        ),
      );
    }
    return out;
  }, [data, store, term]);

  const jobRows = useMemo(() => {
    let out = visits.data?.jobs ?? [];
    if (store !== ALL_STORES) out = out.filter((r) => r.customer_id === store);
    const q = term.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        [r.description, r.category, r.technician_name, r.store_name]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
    }
    return out;
  }, [visits.data, store, term]);

  const t = data?.totals;
  const brand = data?.master?.group_brand || data?.master?.name || 'your stores';

  return (
    <ChainPage
      title="Service — all stores"
      intro={
        <>
          Open tickets and recent visits across {brand}. The Store column tells you
          which location it belongs to.
        </>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Tile
          label="Open tickets"
          value={isLoading ? '—' : String(t?.service_open ?? 0)}
          tone={(t?.service_open ?? 0) > 0 ? 'warning' : 'default'}
        />
        <Tile label="Tickets on file" value={isLoading ? '—' : String(t?.service_total ?? 0)} />
        <Tile
          label="Recent visits"
          value={visitsEnabled && visits.isLoading ? '—' : String(visits.data?.jobs.length ?? 0)}
          sub={visitsEnabled ? 'last 90 days' : 'open the Visits tab'}
        />
        <Tile label="Stores" value={isLoading ? '—' : String(t?.stores ?? 0)} />
      </div>

      <StoreFilterRow
        stores={data?.stores ?? []}
        store={store}
        onStore={setStore}
        term={term}
        onTerm={setTerm}
        searchPlaceholder="Subject, tech, store…"
      >
        <Pill active={tab === 'tickets'} onClick={() => setTab('tickets')}>
          Tickets
        </Pill>
        <Pill active={tab === 'visits'} onClick={() => setTab('visits')}>
          Visits
        </Pill>
      </StoreFilterRow>

      {tab === 'tickets' ? (
        <div className="rounded-2xl border border-brix-border bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brix-surface-alt/60 text-left text-[11px] uppercase tracking-wide text-brix-muted">
                <tr>
                  <th className="px-4 py-3 font-semibold">Store</th>
                  <th className="px-4 py-3 font-semibold">Request</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Opened</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <EmptyRow colSpan={5}>
                    <Loader2 size={16} className="animate-spin inline-block mr-2" />
                    Loading tickets…
                  </EmptyRow>
                ) : tickets.length === 0 ? (
                  <EmptyRow colSpan={5}>
                    {(data?.service_requests ?? []).length === 0
                      ? 'No store has filed a service request through the portal yet. Anything called or emailed in shows up under Visits once a technician is out.'
                      : 'Nothing matches those filters.'}
                  </EmptyRow>
                ) : (
                  tickets.map((r) => (
                    <tr key={r.id} className="border-t border-brix-border/70">
                      <StoreCell row={r} />
                      <td className="px-4 py-3">
                        <span className="block font-medium text-brix-ink">{r.subject}</span>
                        {r.body && (
                          <span className="block text-[11px] text-brix-muted line-clamp-1">
                            {r.body}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-brix-muted">
                        {kindLabel(r.kind as ServiceRequestKind)}
                      </td>
                      <td className="px-4 py-3 text-brix-muted">{fmtMirrorDate(r.created_at)}</td>
                      <td className="px-4 py-3 text-brix-ink">
                        {statusLabel(r.status as ServiceRequestStatus)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-brix-border bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brix-surface-alt/60 text-left text-[11px] uppercase tracking-wide text-brix-muted">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Store</th>
                    <th className="px-4 py-3 font-semibold">Work</th>
                    <th className="px-4 py-3 font-semibold">Technician</th>
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.isLoading ? (
                    <EmptyRow colSpan={5}>
                      <Loader2 size={16} className="animate-spin inline-block mr-2" />
                      Reading every store's visit history…
                    </EmptyRow>
                  ) : jobRows.length === 0 ? (
                    <EmptyRow colSpan={5}>
                      No service visits on record in the last 90 days.
                    </EmptyRow>
                  ) : (
                    jobRows.map((j) => (
                      <tr key={`${j.customer_id}:${j.job_id}`} className="border-t border-brix-border/70">
                        <StoreCell row={j} />
                        <td className="px-4 py-3">
                          <span className="block font-medium text-brix-ink line-clamp-2">
                            {j.description || j.category || `Job #${j.job_id}`}
                          </span>
                          <span className="block text-[11px] text-brix-muted">#{j.job_id}</span>
                        </td>
                        <td className="px-4 py-3 text-brix-muted">{j.technician_name || '—'}</td>
                        <td className="px-4 py-3 text-brix-muted">
                          {j.completed_at ? fmtMirrorDate(j.completed_at) : '—'}
                        </td>
                        <td className="px-4 py-3 text-brix-ink">{j.status || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* A store we can't read is NOT the same as a store with no visits,
              and only one of those is worth acting on. Say which. */}
          {(visits.data?.unmapped?.length ?? 0) > 0 && (
            <p className="mt-3 flex items-start gap-2 text-xs text-brix-warning">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                Not shown for {visits.data!.unmapped.join(', ')} — those stores aren't
                linked to a service record yet. Ask us and we'll connect them.
              </span>
            </p>
          )}
          {visits.data?.truncated && (
            <p className="mt-2 text-xs text-brix-muted">
              Showing as much history as we could read in one go — reload to continue.
            </p>
          )}
        </>
      )}
    </ChainPage>
  );
}
